import { PaginationData } from "components"
import { useMemo, useState, useEffect } from "react"
import { default_pagination_value } from "utils"
import { TaskList as TaskListRow } from "./types"
import { ModalTaskDetail } from "./components"
import { TaskBoardGrid } from "./components/TaskBoard/TaskBoardGrid"
import { TaskListChrome } from "./components/TaskListChrome"
import { TableTaskList } from "./components/Tables/TaskList"
import { FilterTask } from "./types"
import { useTaskList } from "./utils/hooks"
import { TASK_STATUS } from "./utils/vars"

const TASK_VIEW_STORAGE_KEY = "hawk_task_list_view_mode"

const CREATE_TASK_DRAFT: TaskListRow = {
  task_id: undefined as unknown as number,
  trx_code: "",
  location_id: 0,
  location_name: "",
  departement_id: 0,
  department_name: "",
  project_id: 0,
  project_name: "",
  mr_group_task_id: 0,
  mr_group_task_name: "",
  mr_group_sub_id: 0,
  mr_group_sub_name: "",
  task_status: 1,
  subject: "",
  priority: 1,
  date_start: "",
  date_end: "",
  owner_task_id: 0,
  owner_name: "",
  description: "",
  is_deleted: false,
  company_code: "",
  progress: 0,
  candel: 0
}

type TaskViewMode = "list" | "board"

function readStoredViewMode(): TaskViewMode {
  try {
    const v = localStorage.getItem(TASK_VIEW_STORAGE_KEY)
    if (v === "board" || v === "list") return v
  } catch {
    /* ignore */
  }
  return "board"
}

export default function TaskList() {
  const [viewMode, setViewMode] = useState<TaskViewMode>(readStoredViewMode)
  const [departmentFilter, setDepartmentFilter] = useState("all")
  const [searchDraft, setSearchDraft] = useState("")
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [taskModalData, setTaskModalData] =
    useState<TaskListRow>(CREATE_TASK_DRAFT)

  const [pagination, setPagination] = useState<FilterTask>({
    ...default_pagination_value,
    size: 0,
    pages: 1,
    total: 0,
    task_status: "0",
    priority: "0",
    keyword: ""
  })

  const tasks = useTaskList({
    params: pagination,
    fetchAllForBoard: viewMode === "board",
    onSuccess: (paging) => {
      setPagination((prev) => ({
        ...prev,
        ...paging
      }))
    }
  })

  useEffect(() => {
    const t = window.setTimeout(() => {
      setPagination((prev) => {
        if (prev.keyword === searchDraft) return prev
        return { ...prev, keyword: searchDraft, page: 1 }
      })
    }, 400)
    return () => window.clearTimeout(t)
  }, [searchDraft])

  useEffect(() => {
    if (viewMode !== "list") return
    setPagination((prev) =>
      prev.limit === default_pagination_value.limit
        ? prev
        : { ...prev, limit: default_pagination_value.limit, page: 1 }
    )
  }, [viewMode])

  const status = Object.values(TASK_STATUS)
    .filter((item) => typeof item === "number")
    .map((item) => ({
      value: (item as unknown as number).toString(),
      label: TASK_STATUS[item as number]
    }))
    .concat([
      {
        value: "0",
        label: "All"
      }
    ])

  const filteredData = useMemo(() => {
    let rows = tasks.data ?? []
    if (departmentFilter !== "all") {
      rows = rows.filter(
        (r) => String(r.departement_id) === departmentFilter
      )
    }
    return rows
  }, [tasks.data, departmentFilter])

  const summaryStats = useMemo(() => {
    const rows = filteredData
    const n = rows.length
    let urgentHigh = 0
    let completed = 0
    let progressSum = 0
    for (const r of rows) {
      if (r.priority === 1 || r.priority === 2) urgentHigh += 1
      if (r.task_status === 4) completed += 1
      progressSum += Number(r.progress) || 0
    }
    const avgProgress =
      n > 0 ? Math.round((progressSum / n) * 10) / 10 : 0
    const total =
      departmentFilter === "all"
        ? tasks.paging?.total ?? n
        : n
    return {
      total,
      urgentHigh,
      completed,
      avgProgress
    }
  }, [filteredData, departmentFilter, tasks.paging?.total])

  const tasksForView = useMemo(
    () => ({ ...tasks, data: filteredData }),
    [tasks, filteredData]
  )

  const onClickCreateTask = () => {
    setTaskModalData(CREATE_TASK_DRAFT)
    setTaskModalOpen(true)
  }

  const onOpenTaskDetail = (row: TaskListRow) => {
    setTaskModalData(row)
    setTaskModalOpen(true)
  }

  const setViewModePersist = (mode: TaskViewMode) => {
    setViewMode(mode)
    try {
      localStorage.setItem(TASK_VIEW_STORAGE_KEY, mode)
    } catch {
      /* ignore */
    }
  }

  return (
    <section className="m-5">
      <TaskListChrome
        tasks={tasks}
        summaryStats={summaryStats}
        viewMode={viewMode}
        onViewModeChange={setViewModePersist}
        statusOptions={status}
        statusValue={pagination.task_status}
        onStatusChange={(value) =>
          setPagination({ ...pagination, task_status: value, page: 1 })
        }
        departmentValue={departmentFilter}
        onDepartmentChange={setDepartmentFilter}
        searchValue={searchDraft}
        onSearchChange={setSearchDraft}
        onCreateTask={onClickCreateTask}
      />

      {viewMode === "list" ? (
        <TableTaskList tasks={tasksForView} onOpenTask={onOpenTaskDetail} />
      ) : (
        <TaskBoardGrid tasks={tasksForView} />
      )}

      {viewMode === "list" ? (
        <div className="mt-5">
          <PaginationData
            data={{
              pagination_data: {
                size: tasks.paging?.size
              }
            }}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      ) : null}

      <ModalTaskDetail
        data={taskModalData}
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSuccess={() => {
          tasks.refetch()
          setTaskModalOpen(false)
        }}
      />
    </section>
  )
}
